import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, RefreshCw, UserCheck, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { format, differenceInDays } from 'date-fns';

interface DsarRequest {
  id: string;
  requester_id: string;
  requester_type: string;
  requester_email: string;
  request_type: string;
  status: string;
  message: string | null;
  assigned_admin_id: string | null;
  submitted_at: string;
  deadline_at: string | null;
  completed_at: string | null;
  admin_notes: string | null;
  rejection_reason: string | null;
}

const STATUSES = ['pending', 'in_progress', 'completed', 'rejected'] as const;

const AdminDsarTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [requests, setRequests] = useState<DsarRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [activeRequest, setActiveRequest] = useState<DsarRequest | null>(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('dsar_requests' as any)
      .select('*')
      .order('submitted_at', { ascending: false });

    if (!error && data) {
      setRequests(data as unknown as DsarRequest[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const updateStatus = async (id: string, newStatus: string) => {
    const updates: any = { status: newStatus };
    if (newStatus === 'completed') updates.completed_at = new Date().toISOString();

    const { error } = await supabase
      .from('dsar_requests' as any)
      .update(updates)
      .eq('id', id);

    if (error) {
      toast({ title: 'Update Failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Status Updated' });
      fetchRequests();
    }
  };

  const assignToSelf = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('dsar_requests' as any)
      .update({ assigned_admin_id: user.id, status: 'in_progress' } as any)
      .eq('id', id);

    if (error) {
      toast({ title: 'Assign Failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Assigned to you' });
      fetchRequests();
    }
  };

  const saveNotes = async () => {
    if (!activeRequest) return;
    setSavingNotes(true);
    const { error } = await supabase
      .from('dsar_requests' as any)
      .update({ admin_notes: notesText } as any)
      .eq('id', activeRequest.id);

    if (error) {
      toast({ title: 'Save Failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Notes Saved' });
      setNotesDialogOpen(false);
      fetchRequests();
    }
    setSavingNotes(false);
  };

  const getDeadlineColor = (deadlineStr: string | null) => {
    if (!deadlineStr) return '';
    const days = differenceInDays(new Date(deadlineStr), new Date());
    if (days < 0) return 'text-destructive font-bold';
    if (days <= 7) return 'text-destructive';
    if (days <= 15) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Stats
  const openCount = requests.filter(r => r.status === 'pending' || r.status === 'in_progress').length;
  const overdueCount = requests.filter(r =>
    (r.status === 'pending' || r.status === 'in_progress') &&
    r.deadline_at && differenceInDays(new Date(r.deadline_at), new Date()) < 0
  ).length;
  const completedRequests = requests.filter(r => r.status === 'completed' && r.submitted_at && r.completed_at);
  const avgResolution = completedRequests.length > 0
    ? Math.round(completedRequests.reduce((sum, r) => sum + differenceInDays(new Date(r.completed_at!), new Date(r.submitted_at)), 0) / completedRequests.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{openCount}</div>
            <div className="text-sm text-muted-foreground">{t('dataManagement.dsar.openRequests', 'Open Requests')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className={`text-2xl font-bold ${overdueCount > 0 ? 'text-destructive' : ''}`}>{overdueCount}</div>
            <div className="text-sm text-muted-foreground">{t('dataManagement.dsar.overdue', 'Overdue')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{avgResolution}d</div>
            <div className="text-sm text-muted-foreground">{t('dataManagement.dsar.avgResolution', 'Avg Resolution')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('dataManagement.dsar.title', 'DSAR Requests')}</CardTitle>
            <CardDescription>{t('dataManagement.dsar.description', 'Manage data subject access requests')}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('dataManagement.refresh', 'Refresh')}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : requests.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t('dataManagement.dsar.noRequests', 'No DSAR requests yet.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requester</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell>
                        <div className="text-sm">{req.requester_email}</div>
                        <div className="text-xs text-muted-foreground">{req.requester_type}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{req.request_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={req.status}
                          onValueChange={(val) => updateStatus(req.id, val)}
                        >
                          <SelectTrigger className="w-[130px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map(s => (
                              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(req.submitted_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className={`text-sm ${getDeadlineColor(req.deadline_at)}`}>
                        {req.deadline_at ? format(new Date(req.deadline_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!req.assigned_admin_id && (
                            <Button variant="ghost" size="sm" onClick={() => assignToSelf(req.id)} title="Assign to me">
                              <UserCheck className="h-4 w-4" />
                            </Button>
                          )}
                          <Dialog open={notesDialogOpen && activeRequest?.id === req.id} onOpenChange={(open) => {
                            setNotesDialogOpen(open);
                            if (open) {
                              setActiveRequest(req);
                              setNotesText(req.admin_notes || '');
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" title="Admin Notes">
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Admin Notes</DialogTitle>
                              </DialogHeader>
                              <Textarea value={notesText} onChange={(e) => setNotesText(e.target.value)} rows={4} />
                              <Button onClick={saveNotes} disabled={savingNotes}>
                                {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Save Notes
                              </Button>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDsarTab;
